import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useTumUtilityAccounts } from "@/hooks/useTumUtilityAccounts";
import { useTumProperties } from "@/hooks/useTumProperties";
import { useTumUtilityVendors } from "@/hooks/useTumUtilityVendors";

const accountSchema = z.object({
  account_number: z.string().min(1, "Account number is required"),
  commodity: z.string(),
  property_id: z.string().uuid("Please select a property"),
  vendor_id: z.string().uuid().optional(),
  status: z.string().optional(),
});

type AccountFormData = z.infer<typeof accountSchema>;

interface TumUtilityAccountFormProps {
  account?: any;
  onSuccess?: () => void;
}

export const TumUtilityAccountForm = ({ account, onSuccess }: TumUtilityAccountFormProps) => {
  const { createAccount, updateAccount, isCreating, isUpdating } = useTumUtilityAccounts();
  const { properties } = useTumProperties();
  const { vendors } = useTumUtilityVendors();

  const form = useForm<AccountFormData>({
    resolver: zodResolver(accountSchema),
    defaultValues: account ? {
      account_number: account.account_number,
      commodity: account.commodity || 'electric',
      property_id: account.property_id || '',
      vendor_id: account.vendor_id || '',
      status: account.status || 'active',
    } : {
      commodity: 'electric',
      status: 'active',
    },
  });

  const onSubmit = (data: AccountFormData) => {
    if (account) {
      updateAccount({ id: account.id, ...data as any });
      onSuccess?.();
    } else {
      createAccount(data as any);
      form.reset();
      onSuccess?.();
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="account_number"
            render={({ field }) => (
              <FormItem className="col-span-2">
                <FormLabel>Account Number *</FormLabel>
                <FormControl>
                  <Input placeholder="123456789" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="commodity"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Commodity *</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select commodity" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="electric">Electric</SelectItem>
                    <SelectItem value="gas">Gas</SelectItem>
                    <SelectItem value="water">Water</SelectItem>
                    <SelectItem value="internet">Internet</SelectItem>
                    <SelectItem value="sewage">Sewage</SelectItem>
                    <SelectItem value="trash">Trash</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="status"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Status *</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="pending_setup">Pending Setup</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                    <SelectItem value="transferred">Transferred</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="property_id"
            render={({ field }) => (
              <FormItem className="col-span-2">
                <FormLabel>Property *</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select property" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {properties.map(property => (
                      <SelectItem key={property.id} value={property.id}>
                        {property.property_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="vendor_id"
            render={({ field }) => (
              <FormItem className="col-span-2">
                <FormLabel>Utility Vendor</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select vendor (optional)" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {vendors.map(vendor => (
                      <SelectItem key={vendor.id} value={vendor.id}>
                        {vendor.vendor_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="flex justify-end gap-3 pt-4">
          <Button type="button" variant="outline" onClick={onSuccess}>
            Cancel
          </Button>
          <Button type="submit" disabled={isCreating || isUpdating}>
            {account ? 'Update' : 'Create'} Account
          </Button>
        </div>
      </form>
    </Form>
  );
};
